import { DEFAULT_MODEL_FOR_ASSESSMENT_QUERY } from "../components/AISettings";

import { serverPostAssessmentQuery } from "api/api-server-ai";
import { AIModelType, AIServerMessageType, SubmissionType } from "components/server-types";
import { t } from "i18n/i18n";
import { store } from "store/index";
import { selectCurrentSectionId, selectNestedPageOutcomes } from "store/selectors";
import { rollbarAndLogError } from "utils/logger";

export type AIAssessmentResponseType = {
  outcomes: { pageOutcomeId: string; score: number }[];
  feedback: string;
  refused?: string;
  errorMessage?: string;
};

export const serverAssessSubmission = async (
  submission: SubmissionType,
  model: AIModelType = DEFAULT_MODEL_FOR_ASSESSMENT_QUERY,
): Promise<AIAssessmentResponseType> => {
  try {
    const state = store.getState();
    const sectionId = selectCurrentSectionId(state);
    const pageId = submission.page_id;
    const pageOutcomes = selectNestedPageOutcomes(state, pageId);

    const prompt: AIServerMessageType[] = [
      {
        type: "user",
        role: "user",
        content: ["Please help me with assessing the current student submission."].join("\n\n"),
      },
    ];
    const data = await serverPostAssessmentQuery({
      section_id: sectionId,
      submission_id: submission.id,
      model,
      prompt,
    });

    const response = data.result;

    if (response.refused) {
      return {
        outcomes: [],
        feedback: "",
        refused: t("assessment_card.ai.refused", { reason: response.refused }),
      };
    }

    const outcomes = Object.keys(response.outcomes)
      .map((outcomeName) => {
        const outcomeTitle = outcomeName.replaceAll("#", "");
        const pageOutcome = pageOutcomes.find((po) => po.outcome.title === outcomeTitle);
        if (!pageOutcome) {
          return null;
        }
        return {
          pageOutcomeId: pageOutcome.id,
          score: Number(response.outcomes[outcomeName]),
        };
      })
      .filter((o): o is { pageOutcomeId: string; score: number } => o != null);

    if (pageOutcomes.length > 0 && outcomes.length < pageOutcomes.length) {
      rollbarAndLogError("serverAssessSubmission: partial outcome mapping.", {
        expected: pageOutcomes.length,
        received: outcomes.length,
      });
      return {
        outcomes: [],
        feedback: "",
        errorMessage: t("assessment_card.ai.error"),
      };
    }

    return {
      outcomes,
      feedback: (response.feedback ?? "").trim(),
    };
  } catch (error) {
    rollbarAndLogError("serverAssessSubmission: server error.", error);
    return {
      outcomes: [],
      feedback: "",
      errorMessage: t("assessment_card.ai.error"),
    };
  }
};
